import {CircularProgress, Dialog, DialogContent, DialogTitle, InputAdornment, Typography} from '@mui/material';
import React, {FunctionComponent, useEffect, useMemo, useState} from 'react';
import {SubmitHandler, useForm, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import Swal from 'sweetalert2';
import {closeModalIcon, errorBigIcon, toastSuccess} from '../../../../assets/icons/icons';
import mfaIco from '../../../../assets/images/mfa-icon.png';
import ValidationErrorBlock from '../../../../components/Blocks/ValidationErrorBlock/ValidationErrorBlock';
import IsVisibleButton from '../../../../components/Buttons/IsVisibleButton/IsVisibleButton';
import {Button, FormControl, TextField} from '../../../../components/StyledComponents';
import {MAX_INPUT_VALUE_LENGTH, PASSWORD_REGEX} from '../../../../models/consts';
import {IPasswordsVisiblity} from '../../../../models/inner-models';
import {IChangePassword} from '../../../../models/models';
import {changePassword} from '../../../../store/actions';
import {useAppDispatch} from '../../../../store/hooks';
import {decoratePopUpMessage} from '../../../../utils/popUpTextDecorator';
import {mfaEnabled} from '../../../../cognito/cognitoTest';
import {Lock} from 'iconsax-reactjs';
import AuthCode from 'react-auth-code-input';
import '../style.scss';
import '../TwoFactorAuthentication/TwoFAModal/style.scss';
import {zxcvbn, zxcvbnOptions} from '@zxcvbn-ts/core';
import * as zxcvbnCommonPackage from '@zxcvbn-ts/language-common';
import * as zxcvbnEnPackage from '@zxcvbn-ts/language-en';

const zxcvbnConfigOptions = {
  translations: zxcvbnEnPackage.translations,
  graphs: zxcvbnCommonPackage.adjacencyGraphs,
  dictionary: {
    ...zxcvbnCommonPackage.dictionary,
    ...zxcvbnEnPackage.dictionary,
  },
};
zxcvbnOptions.setOptions(zxcvbnConfigOptions);

const ChangePasswordForm: FunctionComponent = () => {
  const {register, reset, formState: {errors, isValid}, handleSubmit, control} = useForm<IChangePassword>({
    mode: 'onChange',
  });
  const dispatch = useAppDispatch();
  const {t: translate} = useTranslation();
  const [visible, setPasswordVisiblity] = useState<IPasswordsVisiblity>({
    password: false,
    confirmPassword: false,
    oldPassword: false,
  });
  const [notMatchError, setNotMatchError] = useState<boolean>(false);
  const [formKey, setFormKey] = useState<number>(0);
  const [isMfaEnabled, setIsMfaEnabled] = useState<boolean>(false);
  const [isMfaModalOpen, setIsMfaModalOpen] = useState<boolean>(false);
  const [mfaCode, setMfaCode] = useState<string>('');
  const [mfaError, setMfaError] = useState<string>('');
  const [mfaInpKey, setMfaInpKey] = useState<number>(0);
  const [isMfaSubmitting, setIsMfaSubmitting] = useState<boolean>(false);
  const [pendingFormData, setPendingFormData] = useState<IChangePassword | null>(null);

  const mainPass = useWatch({control, name: 'password'});
  const confirmPass = useWatch({control, name: 'confirmPassword'});

  useEffect(() => {
    mfaEnabled().then((enabled) => setIsMfaEnabled(Boolean(enabled)));
  }, []);

  const handleClickShowPassword = (field: keyof IPasswordsVisiblity) => () => {
    setPasswordVisiblity({...visible, [field]: !visible[field]});
  };

  const handleCloseMfaModal = () => {
    if (!isMfaSubmitting) {
      setIsMfaModalOpen(false);
      setMfaError('');
      setMfaCode('');
      setPendingFormData(null);
    }
  };

  const zxcvbnResult = useMemo(() => {
    return mainPass?.length ? zxcvbn(mainPass) : null;
  }, [mainPass]);

  const hasDictionaryWords = useMemo(() => {
    return Boolean(zxcvbnResult?.sequence?.some((i) => i.pattern === 'dictionary'));
  }, [zxcvbnResult]);

  const hasRepeat = useMemo(() => {
    return Boolean(zxcvbnResult?.sequence?.some((i) => i.pattern === 'repeat'));
  }, [zxcvbnResult]);

  const isPasswordValid = useMemo(() => {
    if (!mainPass) return false;
    return PASSWORD_REGEX.test(mainPass) && !hasDictionaryWords && !hasRepeat;
  }, [mainPass, hasDictionaryWords, hasRepeat]);

  useEffect(() => {
    if (mainPass && confirmPass && mainPass !== confirmPass) {
      setNotMatchError(true);
    } else {
      setNotMatchError(false);
    }
  }, [mainPass, confirmPass]);

  const doChangePassword = (data: IChangePassword) => {
    setIsMfaSubmitting(true);
    dispatch(changePassword(data))
      .unwrap()
      .then(() => {
        setIsMfaModalOpen(false);
        setIsMfaSubmitting(false);
        setPendingFormData(null);
        setMfaCode('');
        setMfaError('');
        Swal.fire({
          title: translate('notifications.titles.success'),
          text: 'Your password was changed',
          toast: true,
          position: 'top-end',
          timerProgressBar: true,
          showConfirmButton: false,
          showCloseButton: true,
          imageUrl: toastSuccess,
          timer: 3000,
        });
        reset();
        setFormKey(prevState => prevState + 1);
      })
      .catch((err: any) => {
        setIsMfaSubmitting(false);
        if (err?.mfaRequired) {
          setIsMfaEnabled(true);
          setPendingFormData(data);
          setMfaCode('');
          setMfaError('');
          setMfaInpKey(Math.random());
          setIsMfaModalOpen(true);
          return;
        }

        let errorMsg = err?.error || err?.message || 'Request failed';
        if (
          errorMsg === 'Code mismatch' ||
          errorMsg === 'Invalid code received for user' ||
          errorMsg?.includes('Code mismatch') ||
          errorMsg?.includes('Invalid code')
        ) {
          errorMsg = 'Code is invalid, please check your authenticator';
        } else if (
          errorMsg === 'Invalid session for the user, session is expired.' ||
          errorMsg?.includes('session is expired')
        ) {
          errorMsg = 'Code has expired, please check your authenticator';
        } else if (
          errorMsg === 'Your software token has already been used once.' ||
          errorMsg?.includes('software token has already been used')
        ) {
          errorMsg = 'Please wait for your app to refresh a new code';
        }

        if (isMfaModalOpen) {
          setMfaError(errorMsg);
          setMfaCode('');
          setMfaInpKey(Math.random());
        } else {
          Swal.fire({
            title: translate('notifications.titles.error'),
            text: decoratePopUpMessage(errorMsg),
            imageUrl: errorBigIcon,
            confirmButtonText: translate('notifications.choices.close'),
          });
        }
      });
  };

  const onSubmit: SubmitHandler<IChangePassword> = (data) => {
    if (!notMatchError && isPasswordValid) {
      if (isMfaEnabled) {
        setPendingFormData(data);
        setMfaCode('');
        setMfaError('');
        setMfaInpKey(Math.random());
        setIsMfaModalOpen(true);
        return;
      }
      doChangePassword(data);
    }
  };

  const handleConfirmMfa = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!pendingFormData || mfaCode.length < 6) return;
    doChangePassword({...pendingFormData, mfaCode});
  };

  return (
    <div className="settings-block-container password-block">
      <div className="block-label">
        <Typography variant="h2">Account Security</Typography>
        <div className="block-label-desc">Keep your Backups protected with several layers of authentication.</div>
      </div>
      <form className="password-form" noValidate onSubmit={handleSubmit(onSubmit)} key={formKey}>
        <div className="form-row col-2">
          <FormControl>
            <div className="label-block">{translate('forms.change_password.current_password')}</div>
            <TextField
              required
              placeholder={translate('forms.change_password.current_password')}
              id="old-password"
              type={visible.oldPassword ? 'text' : 'password'}
              {...register('oldPassword', {
                required: translate('forms.common.required') as string,
                maxLength: {
                  value: 100,
                  message: translate('forms.common.max_length'),
                },
              })}
              error={!!errors.oldPassword}
              sx={{'& > div': {paddingRight: '32px'}}}
              InputProps={{
                endAdornment:
                  <InputAdornment position="end">
                    <IsVisibleButton
                      onClick={handleClickShowPassword('oldPassword')}
                      isNowVisible={visible.oldPassword}
                    />
                  </InputAdornment>,
              }}
            />
            {errors.oldPassword &&
              <ValidationErrorBlock errorMessage={errors.oldPassword.message as string}/>
            }
          </FormControl>
        </div>
        <div className="form-row col-2">
          <FormControl>
            <div className="label-block">{translate('forms.common.password')}</div>
            <TextField
              placeholder={translate('forms.common.password')}
              required
              id="password"
              type={visible.password ? 'text' : 'password'}
              {...register('password', {
                required: translate('forms.common.required') as string,
                maxLength: {
                  value: MAX_INPUT_VALUE_LENGTH,
                  message: translate('forms.common.max_length'),
                },
                pattern: {
                  value: PASSWORD_REGEX,
                  message: translate('forms.common.invalid_password'),
                },
              })}
              error={!!errors.password}
              sx={{'& > div': {paddingRight: '32px'}}}
              InputProps={{
                endAdornment:
                  <InputAdornment position="end">
                    <IsVisibleButton
                      onClick={handleClickShowPassword('password')}
                      isNowVisible={visible.password}
                    />
                  </InputAdornment>,
              }}
            />
            {errors.password && <ValidationErrorBlock errorMessage={errors.password.message as string}/>}
            {(!!mainPass?.length) && (
              <ul className={mainPass?.length === 0 ? 'auth-password-help validation' : 'auth-password-help'}>
                <li className={mainPass?.length > 5 ? 'success' : 'error'}>
                  6 characters minimum
                </li>
                <li className={hasDictionaryWords || hasRepeat ? 'error' : 'success'}>
                  No dictionary words, repetitions and commonly used passwords
                </li>
                <li className={!/\s/.test(mainPass) ? 'success' : 'error'}>
                  No spaces
                </li>
                <li className={/^(.*[0-9].*)$/.test(mainPass) ? 'success' : 'error'}>
                  One number
                </li>
                <li className={/^(?=.*[a-z])(?=.*[A-Z]).*$/.test(mainPass) ? 'success' : 'error'}>
                  At least one uppercase and one lowercase
                </li>
                {!isPasswordValid && (
                  <li className={'error'}>
                    {translate('forms.common.invalid_password')}
                  </li>
                )}
              </ul>
            )}
          </FormControl>
        </div>
        <div className="form-row col-2">
          <FormControl>
            <div className="label-block">{translate('forms.common.confirm_password')}</div>
            <TextField
              placeholder={translate('forms.common.confirm_password')}
              required
              id="confirm-password"
              type={visible.confirmPassword ? 'text' : 'password'}
              {...register('confirmPassword', {
                required: translate('forms.common.required') as string,
                pattern: {
                  value: PASSWORD_REGEX,
                  message: translate('forms.common.invalid_password'),
                },
                maxLength: {
                  value: 100,
                  message: translate('forms.common.max_length'),
                },
              })}
              sx={{'& > div': {paddingRight: '32px'}}}
              InputProps={{
                endAdornment:
                  <InputAdornment position="end">
                    <IsVisibleButton
                      onClick={handleClickShowPassword('confirmPassword')}
                      isNowVisible={visible.confirmPassword}
                    />
                  </InputAdornment>,
              }}
              error={!!errors.confirmPassword}
            />
            {(errors?.confirmPassword || notMatchError) &&
              <ValidationErrorBlock
                errorMessage={errors?.confirmPassword?.message || translate('forms.common.passwords_dont_match')}/>
            }
          </FormControl>
        </div>
        <div className="action-row">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!isValid || notMatchError || !isPasswordValid}
          >
            <Lock size={16} style={{marginRight: '4px'}}/>
            {translate('forms.change_password.change_password')}
          </Button>
        </div>
      </form>

      <Dialog
        open={isMfaModalOpen}
        onClose={handleCloseMfaModal}
        aria-labelledby="mfa-edit-title"
        aria-describedby="mfa-edit-description"
        classes={{
          root: 'mfa-container',
          paper: 'mfa-modal',
        }}
      >
        <DialogTitle id="mfa-edit-title">
          <img src={mfaIco} loading="lazy" alt="" />
          Two-Factor Authentication
        </DialogTitle>

        <div className="close-btn" onClick={handleCloseMfaModal}>
          <img src={closeModalIcon} loading="lazy" alt="" />
        </div>

        <DialogContent>
          <form onSubmit={handleConfirmMfa}>
            <div id="mfa-edit-description">
              <div className="username-block dis-tex">
                <div className="mfa-add-select-all">
                  <div className="title-line">
                    To change your password, enter the code from your Authenticator app
                  </div>
                  <div className="desc-line" />
                </div>
              </div>

              <div className="scrollable-div">
                <p className="code-input">Enter Code</p>
                <AuthCode
                  key={mfaInpKey}
                  containerClassName="mfa-code-input"
                  allowedCharacters="numeric"
                  onChange={(val) => {
                    setMfaCode(val);
                    setMfaError('');
                  }}
                  disabled={isMfaSubmitting}
                />
                {!!mfaError.length && (
                  <label className="invalid-code">{mfaError}</label>
                )}
              </div>
            </div>

            <div className="action-buttons">
              <Button
                onClick={handleCloseMfaModal}
                variant="outlined"
                color="primary"
                disabled={isMfaSubmitting}
              >
                {translate('notifications.choices.cancel')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                onClick={handleConfirmMfa}
                disabled={isMfaSubmitting || mfaCode.length < 6 || mfaCode.length > 7}
              >
                {isMfaSubmitting ? (
                  <div className="small-spinner-wrapper">
                    <CircularProgress color="inherit" style={{width: '20px', height: '20px', marginTop: '6px'}} />
                  </div>
                ) : (
                  <Lock size={16} style={{marginRight: '4px'}} />
                )}
                Change Password
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ChangePasswordForm;
